import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const OWNER = "Damian Alan Moses";
const YEAR = new Date().getFullYear();

const LegalShell = ({ title, subtitle, updated, children, testid }) => (
  <div className="min-h-screen bg-linen text-ink" data-testid={testid}>
    <header className="flex items-center justify-between px-6 sm:px-10 py-6 max-w-4xl mx-auto">
      <Link to="/" className="font-serif text-3xl text-brand tracking-tight" data-testid="legal-brand">Aurea</Link>
      <Link to="/" className="flex items-center gap-2 text-sm text-ink-2 hover:text-brand transition-colors" data-testid="legal-back">
        <ArrowLeft className="w-4 h-4" /> Home
      </Link>
    </header>
    <main className="max-w-3xl mx-auto px-6 sm:px-10 pb-24 pt-4">
      <p className="text-accent font-medium tracking-wide uppercase text-xs mb-4">Legal</p>
      <h1 className="font-serif text-4xl sm:text-5xl text-ink tracking-tight leading-tight">{title}</h1>
      {subtitle && <p className="mt-4 text-lg text-ink-2 leading-relaxed">{subtitle}</p>}
      <p className="mt-3 text-xs text-ink-mute">Last updated: {updated}</p>
      <div className="mt-10 space-y-8 legal-body">{children}</div>
      <footer className="mt-16 pt-8 border-t border-linen-3 text-sm text-ink-mute">
        <p>© {YEAR} Aurea · {OWNER}. All rights reserved.</p>
        <div className="mt-2 flex gap-4">
          <Link to="/terms" className="hover:text-brand transition-colors" data-testid="legal-link-terms">Terms of Service</Link>
          <Link to="/privacy" className="hover:text-brand transition-colors" data-testid="legal-link-privacy">Privacy Policy</Link>
        </div>
      </footer>
    </main>
  </div>
);

const Section = ({ n, title, children }) => (
  <section>
    <h2 className="font-serif text-2xl text-ink mb-3">{n}. {title}</h2>
    <div className="text-ink-2 leading-relaxed space-y-3 text-[15px]">{children}</div>
  </section>
);

export const Terms = () => (
  <LegalShell
    testid="terms-page"
    title="Terms of Service"
    subtitle="Please read these terms carefully before using Aurea. By creating an account or using the app, you agree to them."
    updated={`${YEAR}`}
  >
    <div className="bg-accent/10 border border-accent/20 rounded-2xl p-5">
      <p className="text-accent font-medium text-sm">
        Aurea is a wellbeing and self-awareness companion. It is NOT a medical device, is NOT a substitute
        for professional diagnosis or treatment, and is NOT an emergency service. If you are in crisis or
        danger, call your local emergency number or a crisis line (in the U.S., dial or text 988) immediately.
      </p>
    </div>

    <Section n="1" title="Acceptance of Terms">
      <p>These Terms of Service ("Terms") form a binding agreement between you and {OWNER}, the sole owner of
      Aurea ("we", "us", "the Owner"). By accessing or using Aurea (the "Service"), you accept these Terms.
      If you do not agree, please do not use the Service.</p>
    </Section>

    <Section n="2" title="Eligibility & Younger Users">
      <p>You must be able to form a binding contract to use Aurea. If you are a minor, you should use Aurea only
      with the involvement and consent of a parent or legal guardian. Aurea's guidance is adapted to be culturally
      and age-aware, but it is intended to support—not replace—the care of trusted adults and professionals.</p>
    </Section>

    <Section n="3" title="Not Medical, Clinical, or Emergency Advice">
      <p>Aurea provides reflective, educational, and self-help content grounded in depth psychology. It does not
      practice medicine, provide therapy, diagnose conditions, or prescribe treatment. AI-generated responses may
      be incomplete or inaccurate. Always seek the advice of a qualified health provider with any questions about
      a medical or mental-health condition. Never disregard professional advice or delay seeking it because of
      something you read in Aurea.</p>
    </Section>

    <Section n="4" title="Safety Monitoring & Crisis Support">
      <p>Aurea may analyze journal entries and messages to detect signs of self-harm, harm to others, or acute
      distress, and may surface crisis resources or, where you have configured it, attempt to notify an emergency
      contact. This safety net is provided on a best-effort basis and may not detect every situation. It is not a
      monitoring or dispatch service and must not be relied upon as one. In an emergency, contact professional
      emergency services directly.</p>
    </Section>

    <Section n="5" title="Your Account & Conduct">
      <p>You are responsible for the accuracy of your information and for keeping your credentials secure. You agree
      not to misuse the Service, attempt to disrupt it, or use it to harm yourself or others in ways the Service is
      not designed to support. We may suspend or terminate accounts that violate these Terms.</p>
    </Section>

    <Section n="6" title="Intellectual Property">
      <p>Aurea, including its source code, design, content, name, and logo, is the sole and exclusive property of
      {" "}{OWNER} and is protected by copyright and other intellectual-property laws. You are granted a limited,
      personal, non-transferable, revocable license to use the Service for your own wellbeing. You may not copy,
      modify, distribute, reverse engineer, or create derivative works from any part of the Service without the
      Owner's prior written permission. You retain ownership of the content you write (your journal entries and
      messages).</p>
    </Section>

    <Section n="7" title="Third-Party Services">
      <p>Aurea uses third-party providers (for example, AI language models and cloud storage) to deliver certain
      features. Your use of those features is also subject to those providers' terms. We are not responsible for
      third-party services.</p>
    </Section>

    <Section n="8" title="Disclaimers & Limitation of Liability">
      <p>THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE", WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED. TO
      THE FULLEST EXTENT PERMITTED BY LAW, THE OWNER SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL,
      CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS ARISING FROM YOUR USE OF, OR INABILITY TO USE, THE SERVICE.</p>
    </Section>

    <Section n="9" title="Changes to These Terms">
      <p>We may update these Terms from time to time. Continued use of the Service after changes take effect
      constitutes acceptance of the revised Terms.</p>
    </Section>

    <Section n="10" title="Contact">
      <p>Questions about these Terms may be directed to the Owner, {OWNER}, through the Service.</p>
    </Section>
  </LegalShell>
);

export const Privacy = () => (
  <LegalShell
    testid="privacy-page"
    title="Privacy Policy"
    subtitle="Your trust and your privacy matter deeply to us. This policy explains what we collect, why, and the control you have over your data."
    updated={`${YEAR}`}
  >
    <div className="bg-calm/60 border border-linen-3 rounded-2xl p-5">
      <p className="text-ink font-medium text-sm">
        You are always in control. You can permanently delete all of your data at any time, in one tap,
        from Settings. When you do, it is erased and cannot be recovered.
      </p>
    </div>

    <Section n="1" title="Who We Are">
      <p>Aurea is owned and operated by {OWNER} ("we", "us"). This policy describes how we handle information when
      you use Aurea (the "Service").</p>
    </Section>

    <Section n="2" title="Information We Collect">
      <p><span className="font-medium text-ink">Account information</span> — such as your name, email, phone
      number (if provided), and preferences like age, ethnicity, and cultural background that you choose to share
      so Aurea can offer culturally-aware support.</p>
      <p><span className="font-medium text-ink">Your content</span> — journal entries, mood check-ins, and messages
      you exchange with the AI companion.</p>
      <p><span className="font-medium text-ink">Safety signals</span> — automated assessments of whether content
      suggests risk of harm, used to surface crisis resources.</p>
      <p><span className="font-medium text-ink">Emergency contact</span> — if you choose to add one, so we can help
      notify them during a detected crisis.</p>
    </Section>

    <Section n="3" title="How We Use Your Information">
      <p>We use your information to provide and personalize the Service, generate reflective guidance, detect and
      respond to safety concerns, maintain your account, and improve reliability. We do not sell your personal
      information.</p>
    </Section>

    <Section n="4" title="Sensitive Health Information">
      <p>Aurea handles emotionally sensitive information. We treat it with heightened care and design the Service to
      limit access to what is necessary to serve you. Aurea is a self-help tool and is not a HIPAA-covered entity
      by default; where the Owner works with covered entities or business associates, additional safeguards and
      agreements would apply. We do not share your content for advertising.</p>
    </Section>

    <Section n="5" title="AI & Third-Party Processing">
      <p>To generate reflections and assess safety, relevant content may be processed by trusted third-party AI
      providers under their data-protection commitments. Uploaded files are stored using secure cloud object
      storage. These providers process data on our behalf to deliver features you request.</p>
    </Section>

    <Section n="6" title="Data Retention & One-Tap Deletion">
      <p>We keep your data only as long as your account is active or as needed to provide the Service. You may
      permanently delete all of your data at any time from Settings. Deletion is immediate and irreversible.</p>
    </Section>

    <Section n="7" title="Security">
      <p>We use reasonable technical and organizational measures to protect your information, including encrypted
      passwords and access controls. No method of transmission or storage is perfectly secure, but we work to keep
      your information safe.</p>
    </Section>

    <Section n="8" title="Your Rights">
      <p>Depending on where you live, you may have rights to access, correct, export, or delete your personal
      information. Aurea's built-in tools let you view your content and delete everything at any time. For other
      requests, contact the Owner through the Service.</p>
    </Section>

    <Section n="9" title="Children's Privacy">
      <p>Aurea is intended to be used by minors only with the involvement and consent of a parent or guardian. We do
      not knowingly collect data from children in violation of applicable law; guardians may request deletion at
      any time.</p>
    </Section>

    <Section n="10" title="Changes & Contact">
      <p>We may update this policy from time to time. Material changes will be reflected by the "Last updated" date
      above. Questions may be directed to the Owner, {OWNER}, through the Service.</p>
    </Section>
  </LegalShell>
);
